import { Close } from "@mui/icons-material";
import { ListItem, ListItemText } from "@mui/material";
import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import { FC, SyntheticEvent } from "react";
import { getHelperText, removeWhiteSpace } from "../../helpers/utils";
import { MuiBox } from "../box/MuiBox";
import { ErrorCrossIcon } from "../errorCrossIcon/ErrorCrossIcon";
import { MuiInfoToolTip } from "../tooltip/infoToolTip/MuiInfoToolTip";
import { MuiTypography } from "../typography/MuiTypography";
import {
  MuiAutoCompleteWrapper,
  StyledAutoCompletePopper,
} from "./MuiAutoComplete.style";

export interface AutoCompleteOption {
  labelTwo?: string;
  label: string;
  id: string;
  clientType?: string;
}
export interface MuiAutoCompleteProps {
  id?: string;
  options: AutoCompleteOption[];
  placeholder?: string;
  requiredARIALabel?: string;
  label?: string;
  isMandatory?: boolean;
  disabled?: boolean;
  showCloseIcon?: boolean;
  value?: AutoCompleteOption | null;
  onChange?: (
    event: SyntheticEvent<Element, Event>,
    value: AutoCompleteOption | null,
  ) => void;
  onBlur?: () => void;
  error?: boolean;
  helperText?: string;
  inputValue?: string;
  setInputValue?: (val: string) => void;
  isInputValueControlled?: boolean;
  noOptionsText?: string;
  infoMessage?: string;
}

export const MuiAutoComplete: FC<MuiAutoCompleteProps> = ({
  id,
  options,
  placeholder = "",
  requiredARIALabel = "",
  label,
  isMandatory = false,
  disabled = false,
  value,
  onChange,
  onBlur,
  error,
  helperText,
  showCloseIcon = false,
  inputValue = "",
  setInputValue,
  isInputValueControlled, // Indicates if inputValue is controlled
  noOptionsText = "",
  infoMessage,
}) => {
  const filterOptions = createFilterOptions<AutoCompleteOption>({
    stringify: (option: AutoCompleteOption) =>
      `${option.label} ${option.labelTwo || ""}`,
  });

  const inputId = `${id}-input`;
  return (
    <MuiAutoCompleteWrapper>
      {label && (
        <MuiBox className="title" id={`${id}-label`}>
          <MuiTypography
            className={disabled ? "mui-label disabled" : "mui-label"}
            component="label"
            variant="h3"
            htmlFor={inputId}
          >
            {label}{" "}
            {isMandatory && !disabled && (
              <span className="asterisk" aria-label={requiredARIALabel}>
                *
              </span>
            )}
          </MuiTypography>
          {infoMessage && (
            <MuiBox className="tooltip-container">
              <MuiInfoToolTip
                message={infoMessage}
                placement="top"
                className="tooltip-wrapper"
              />
            </MuiBox>
          )}
        </MuiBox>
      )}

      <Autocomplete
        id={inputId}
        value={value || null}
        {...(isInputValueControlled
          ? {
              inputValue: inputValue,
              onInputChange: (event, newInputValue, reason) => {
                if (reason !== "reset") {
                  setInputValue?.(newInputValue);
                }
              },
              // open: options.length > 0 && inputValue !== "",
            }
          : {})}
        noOptionsText={noOptionsText}
        PopperComponent={StyledAutoCompletePopper}
        onChange={onChange}
        onBlur={onBlur}
        options={options}
        isOptionEqualToValue={(option, val) => option.id === val?.id}
        renderOption={(props, option: AutoCompleteOption) => (
          <ListItem {...props} component="li" key={option.id}>
            <ListItemText
              primary={
                <MuiTypography variant="h3" className="label-option">
                  {option.label}
                </MuiTypography>
              }
              {...(option.labelTwo && {
                secondary: (
                  <MuiTypography variant="h3" className="label-option">
                    {option.labelTwo}
                  </MuiTypography>
                ),
              })}
            />
          </ListItem>
        )}
        getOptionLabel={(option) => option.label}
        filterOptions={filterOptions}
        clearIcon={showCloseIcon && <Close />}
        disableClearable={false}
        renderInput={(params) => (
          <TextField
            {...params}
            id={inputId}
            className="textbox-auto-complete"
            placeholder={placeholder}
            error={!!error}
            helperText={
              helperText && (
                <MuiBox className="info-box">
                  <MuiBox
                    className="error-icon"
                    aria-description={getHelperText(helperText, label ?? "")}
                  >
                    <ErrorCrossIcon />
                    <MuiBox
                      aria-hidden
                      id={`helper-text-${removeWhiteSpace(label ?? "")}`}
                    >
                      {getHelperText(helperText, label ?? "")}
                    </MuiBox>
                  </MuiBox>
                </MuiBox>
              )
            }
          />
        )}
        disabled={disabled}
      />
    </MuiAutoCompleteWrapper>
  );
};
